import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ResultDisplay } from '../components/LookalikeInteraction';
import { getModelHealth } from '../services/api';
import { trackClarityEvent } from '../utils/clarity';
import { ResultContext } from '../contexts/ResultContext';
import LiftCurveExplanation from '../components/lookalike/LiftCurveExplanation';
import { DownloadIcon } from '@heroicons/react/outline';
import CustomerTable from '../components/CustomerTable';
import VideoEmbed from '../components/VideoEmbed';
import FileUploadDD from '../components/DragDropFile';
import Spinner from '../components/Spinner';
import EChartLiftCurv from '../components/lookalike/EChartLiftCurv';

// --- Helper function to format timestamp string --- 
const formatTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'string' || timestamp === 'N/A') return 'N/A';

  // Handle standard ISO format (e.g., from training updated_at)
  if (timestamp.includes('T')) {
    try {
      return new Date(timestamp).toLocaleString();
    } catch (e) { /* Fall through */ }
  }

  // Handle YYYYMMDD_HHMMSS format (e.g., from prediction metadata)
  const match = timestamp.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    try {
      // Create Date object (month is 0-indexed)
      const date = new Date(year, month - 1, day, hour, minute, second);
      return date.toLocaleString(); // Format based on user's locale
    } catch (e) {
      return timestamp; // Return original if parsing fails
    }
  }

  // Handle simple date strings (e.g., Tue Apr 29 12:54:02 2025)
  try {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  } catch (e) { /* Fall through */ }

  return timestamp; // Return original if no format matches
};
// --- End Helper --- 

const UtilityPage = () => {
  const { uId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { extraData } = location.state || {};
  const [activeTab, setActiveTab] = useState('overview');

  const [modelData, setModelData] = useState(null);

  const [testFile, setTestFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const { result } = useContext(ResultContext);

  // State for dynamic metrics and training info, initialized with defaults
  const [dynamicMetrics, setDynamicMetrics] = useState({
    accuracy: 0,
    precision: 0,
    recall: 0,
    f1: 0, // Renamed from f1Score to match API response
    roc_auc: 0 // Renamed from auc
  });

  // Rename state to be more generic: holds info about the last completed run (train or predict)
  const [runInfo, setRunInfo] = useState({
    runType: 'N/A',
    lastRun: 'N/A',
    dataSize: 'N/A',
    duration: 'N/A',
    modelVersion: 'N/A',
    modelType: 'N/A' // Add modelType here too
  });

  // Placeholder data for models
  const modelTypes = {
    'email': {
      name: 'Email Validation',
      description: 'Email validation description.',
      accuracy: 92,
      status: 'Active',
      lastUpdated: '2023-10-28',
      trainedOn: '2023-10-15',
      features: [
        'Customer segmentation based on behavior',
        'Predictive analytics for acquisition',
        'Similar audience targeting',
        'Conversion probability scoring',
        'Customer value prediction'
      ],
      metrics: {
        precision: 0.89,
        recall: 0.92,
        f1Score: 0.90,
        auc: 0.94
      },
      visualization: {
        type: 'Customer Clusters'
      }
    },
    'analytical': {
      name: 'File Analytical Model',
      description: 'File Analytical Description',
      accuracy: 88,
      status: 'Active',
      lastUpdated: '2023-10-25',
      trainedOn: '2023-10-10',
      features: [
        'Purchase recency analysis',
        'Frequency patterns detection',
        'Monetary value assessment',
        'Customer lifecycle stage identification',
        'Engagement scoring'
      ],
      metrics: {
        precision: 0.87,
        recall: 0.84,
        f1Score: 0.85,
        auc: 0.91
      },
      visualization: {
        type: 'Heatmap Analysis'
      }
    }
  };

  // Mapping uId to the keys in modelTypes and API endpoints
  const modelIdToKey = {
    '7': 'email',
    '8': 'analytical'

  };

  const key = modelIdToKey[uId] || '';

  // Function to handle tab changes and track in Clarity
  const handleTabChange = (tab) => {
    if (tab !== activeTab) {
      trackClarityEvent('model_tab_change', {
        modelId: uId,
        modelType: key,
        previousTab: activeTab,
        newTab: tab
      });
      setActiveTab(tab);
    }
  };

  useEffect(() => {
    setModelData({ ...modelTypes[key], status: 'Loading...' });
    // Set initial model type based on key
    setRunInfo(prev => ({ ...prev, modelType: key }));

    //setModelData(extraData)
  }, [key, uId]);





  useEffect(() => {
    if (!modelData) return;

    const checkModelHealth = async () => {
      try {
        const healthData = await getModelHealth(key);


        let status = 'Unknown';

        // Update model status based on health check
        if (healthData && healthData.status === 'healthy') {
          if (healthData.services?.model?.status === 'active') {
            status = 'Active';
          } else if (healthData.services?.model?.status === 'training') {
            status = 'In Training';
          }
        } else {
          status = 'Offline';
        }

        // Track model health status in Clarity
        trackClarityEvent('model_health_check', {
          modelId: uId,
          modelType: key,
          status: status,
          healthStatus: healthData?.status || 'unknown'
        });

        // Update model data with the new status
        setModelData(prevData => ({
          ...prevData,
          status
        }));
      } catch (error) {
        console.error(`Error fetching health for ${key} model:`, error);

        // Track error in Clarity
        trackClarityEvent('model_health_error', {
          modelId: uId,
          modelType: key,
          errorMessage: error.message
        });

        setModelData(prevData => ({
          ...prevData,
          status: 'Error'
        }));
      }
    };

    checkModelHealth();
  }, [key, uId, modelData?.model_name]); // Added modelData.name dependency





  const primaryButtonClass = "px-4 py-2 mt-10 border border-transparent text-sm font-medium rounded-md text-white bg-[#0062cc] hover:bg-[#0052ac] disabled:opacity-50 disabled:cursor-not-allowed";


  // New callback for Prediction completion


  // Return early if model data is not yet loaded
  if (!modelData) return <div>Loading...</div>;

  // Merge static description with dynamic status for display
  const displayStatus = modelData.status || 'Unknown';
  // Format the last run time for display
  const displayLastRun = formatTimestamp(runInfo.lastRun);

  let parsedLiftData = [];



  if (result) {
    // --- Display Prediction Results --- 

    const liftMetrics = result.lift_metrics;


    // --- Parse lift data --- 

    if (liftMetrics?.lift_values) {
      try {
        const rawLiftData = JSON.parse(liftMetrics.lift_values);

        if (rawLiftData.index && rawLiftData['Lift (Total Sales)'] && rawLiftData['Cumulative Lift']) {
          parsedLiftData = rawLiftData.index.map((idx, i) => ({
            index: idx,
            'Lift (Total Sales)': rawLiftData['Lift (Total Sales)'][i] || 0,
            'Cumulative Lift': rawLiftData['Cumulative Lift'][i] || 0,
          }));
        }
      } catch (parseError) {
        console.error("Error parsing lift_values JSON:", parseError);
      }
    }
    // --- End Parse lift data --- 
  }


  return (
    <div className="pt-24 pb-16 px-4 md:px-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        <div className="flex items-center mb-8">
          <button
            onClick={() => {
              trackClarityEvent('model_navigation', {
                action: 'back_to_dashboard',
                modelId: uId,
                modelType: key
              });
              navigate('/models');
            }}
            className="mr-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{modelData.name}</h1>
            <div className="flex items-center mt-2">
              <span className={`px-2 py-1 text-xs rounded-full mr-3 ${displayStatus === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                displayStatus === 'In Training' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                  displayStatus === 'Offline' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                }`}>
                {displayStatus}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">Last Run: {displayLastRun}</span>
            </div>
          </div>
        </div>

        {/* <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden mb-8">
          <div className="p-6">
            <p className="text-gray-600 dark:text-gray-300">{modelData.description}</p>
          </div>
        </div> */}

        <div className="mb-6">
          <nav className="flex space-x-6 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => handleTabChange('overview')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'overview'
                ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => handleTabChange('performance')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'performance'
                ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Visualization
            </button>
            <button
              onClick={() => handleTabChange('settings')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'settings'
                ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              Insights
            </button>

            <button
              onClick={() => handleTabChange('demo')}
              className={`py-4 px-1 text-sm font-medium ${activeTab === 'demo'
                ? 'text-[#0062cc] border-b-2 border-[#0062cc]'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
            >
              View Demo
            </button>
          </nav>
        </div>

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 h-full"
              >
                {key === 'analytical' ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Analytical File Upload</h3>
                    <div className='col-span-full flex items-center justify-center text-center h-64'>
                      <FileUploadDD />
                    </div>
                  </div>
                ) :
                  key === 'email' ? (
                    <div>

                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Upload Email Validation Model</h3>
                      <div className="col-span-full py-1">


                        <p className="text-sm text-gray-600 dark:text-gray-300">Select your testing data file (.csv expected).</p>
                        <>
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => { setTestFile(e.target.files[0]); setUploadProgress(0); }}
                            className="block w-full mt-7 text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-semibold file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-200 dark:hover:file:bg-gray-600"
                          />
                          {/* Upload Progress Bar */}
                          {isLoading && uploadProgress > 0 && (
                            <div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 my-1">
                                <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                              </div>
                              <p className="text-xs text-center text-gray-600 dark:text-gray-400">Uploading: {uploadProgress}%</p>
                            </div>
                          )}
                          <button
                            onClick={() => { }//handleUpload('testing')
                            }
                            disabled={!testFile || isLoading}
                            className={primaryButtonClass}
                          >
                            {isLoading ? <><Spinner size="xs" className="mr-2" /> Uploading...</> : 'Upload File'}
                          </button>
                        </>
                      </div>
                    </div>
                  ) :
                    (<div>Default</div>)}
              </motion.div>
            </div>

            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6"
              >
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Model Performance</h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Accuracy</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-white">{(dynamicMetrics.accuracy * 100).toFixed(0)}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.accuracy * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Precision</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.precision.toFixed(2)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.precision * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-300">Recall</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.recall.toFixed(2)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.recall * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-300">F1 Score</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.f1.toFixed(2)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.f1 * 100}%` }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-600 dark:text-gray-300">ROC AUC</span>
                      <span className="text-sm font-medium text-gray-800 dark:text-white">{dynamicMetrics.roc_auc.toFixed(2)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div className="h-full bg-[#0062cc] rounded-full" style={{ width: `${dynamicMetrics.roc_auc * 100}%` }}></div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700"
              >
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                  {runInfo.runType === 'Prediction' ? 'Prediction Information' : 'Training Information'}
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Last Run Time</p>
                    <p className="text-gray-800 dark:text-white">{displayLastRun}</p>
                  </div>
                  {/* Show model version only if it was a prediction run */}
                  {runInfo.runType === 'Prediction' && runInfo.modelVersion !== 'N/A' && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Model Version Used</p>
                      <p className="text-gray-800 dark:text-white">{runInfo.modelVersion}</p>
                    </div>
                  )}
                  {/* Show model type (algorithm) */}
                  {(runInfo.modelType && runInfo.modelType !== 'N/A') && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Model Type / Algorithm</p>
                      <p className="text-gray-800 dark:text-white capitalize">{runInfo.modelType?.replace('_', ' ') || 'N/A'}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Input Data Size</p>
                    <p className="text-gray-800 dark:text-white">{runInfo.dataSize}</p>
                  </div>
                  {/* Show duration only if it was a training run */}
                  {runInfo.runType === 'Training' && runInfo.duration !== 'N/A' && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Run Duration</p>
                      <p className="text-gray-800 dark:text-white">{runInfo.duration}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">

            {/* <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Performance Metrics</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">Detailed analysis of model performance across different metrics and time periods.</p>

            <div className="h-80 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg mb-6">
              <div className="text-center px-4">
                <p className="text-gray-400 dark:text-gray-500 mb-2">Placeholder for Performance Charts</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Upgrade your plan to access advanced performance analytics</p>
              </div>
            </div> */}

            <div>

              {key === 'rfm' && <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Recency Frequency Monetory Metrics</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6">Detailed analysis of model performance across different metrics and time periods.</p>






              </div>}

              {/* Lift Curve Visualization & Explanation */}
              {(result?.lift_curve_explanation /* Keep condition for layout */) && (
                <div className="mt-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                  <h4 className="font-semibold text-md mb-3 text-gray-800 dark:text-white">Lift Curve Analysis</h4>
                  {parsedLiftData.length > 0 ? (
                    <EChartLiftCurv data={parsedLiftData} />
                  ) : (
                    <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-4">Could not load lift curve data.</p>
                  )}

                  {/* --- Render the new Explanation component --- */}
                  <div className="mt-4">
                    <LiftCurveExplanation />
                  </div>
                  {/* --- End new component rendering --- */}
                </div>
              )}

              {/* Sample Predictions Preview */}
              {/* {JSON.stringify(result)} */}
              {result?.preview_data && (
                <ResultDisplay title={`Sample Predictions (Showing ${result?.preview_data.summary?.total_samples || 'some'} customers)`} data={result?.preview_data.preview_html} />
              )}

              {/* Download Button */}
              {result?.predictions_path && (
                <div className="mt-6 text-center">
                  <a
                    href={result?.predictions_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${primaryButtonClass} inline-flex items-center`}
                  >
                    <DownloadIcon className="w-5 h-5 mr-2" />
                    Download Full Predictions (.csv)
                  </a>
                </div>
              )}

            </div>

            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-3">Metric Definitions</h4>
                <div className="space-y-2">
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="block font-medium text-gray-800 dark:text-white mb-1">Accuracy</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">The proportion of predictions that the model got right.</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="block font-medium text-gray-800 dark:text-white mb-1">Precision</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">The proportion of positive identifications that were actually correct.</p>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="block font-medium text-gray-800 dark:text-white mb-1">Recall</span>
                    <p className="text-sm text-gray-600 dark:text-gray-300">The proportion of actual positives that were identified correctly.</p>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-3">Performance Over Time</h4>
                <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">Model performance has been consistent since deployment with minor fluctuations during data updates.</p>
                  <div className="flex flex-col space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Initial Training</span>
                      <span className="text-xs font-medium text-gray-800 dark:text-white">{modelData.accuracy - 5}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">After First Update</span>
                      <span className="text-xs font-medium text-gray-800 dark:text-white">{modelData.accuracy - 2}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">Current</span>
                      <span className="text-xs font-medium text-gray-800 dark:text-white">{modelData.accuracy}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
             */}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">

            {key === 'rfm' && <div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">Implement loyalty program and personalized communication</p>

              <div className="p-10 bg-gray-100 min-h-screen">
                <CustomerTable customers={result?.celery_status?.result?.sample_customers} />
              </div>
            </div>
            }

            {/* Key Takeaways */}
            {result?.key_takeaways && (
              <ResultDisplay title="Key Takeaways" data={result?.key_takeaways} />
            )}

            {/* Customer Profile Insights */}
            {result?.customer_profile_insights && (
              <ResultDisplay title="Customer Profile Insights" data={result?.customer_profile_insights} />
            )}

            {/* <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Model Settings</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">Configure model parameters and settings for optimal performance.</p>

            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-3">Training Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Training Frequency</label>
                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-white">
                      <option>Weekly</option>
                      <option>Bi-weekly</option>
                      <option>Monthly</option>
                      <option>Quarterly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Source</label>
                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-white">
                      <option>All Data</option>
                      <option>Last 30 Days</option>
                      <option>Last 90 Days</option>
                      <option>Last 12 Months</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-3">Model Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confidence Threshold</label>
                    <input type="range" min="0" max="100" value="75" className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer" />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Feature Importance</label>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-300">Recency</span>
                        <span className="text-xs font-medium text-gray-800 dark:text-white">High</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-300">Frequency</span>
                        <span className="text-xs font-medium text-gray-800 dark:text-white">Medium</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-300">Monetary Value</span>
                        <span className="text-xs font-medium text-gray-800 dark:text-white">High</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Reset to Default
                </button>
                <button className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0062cc] hover:bg-[#0052ac]">
                  Save Changes
                </button>
              </div>
            </div>
             */}
          </div>
        )}

        {activeTab === 'demo' && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">




            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">{extraData.model_name}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                {extraData?.youtube_url && <div>
                  <div>

                    <VideoEmbed url={extraData?.youtube_url} />
                  </div>
                </div>}
              </div>


              <div>

                <p className="text-gray-600 dark:text-gray-300 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <div dangerouslySetInnerHTML={{ __html: extraData?.model_desc }}></div>
                </p>
              </div>
            </div>


            {/* <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-3">Training Settings</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Training Frequency</label>
                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-white">
                      <option>Weekly</option>
                      <option>Bi-weekly</option>
                      <option>Monthly</option>
                      <option>Quarterly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Source</label>
                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-white">
                      <option>All Data</option>
                      <option>Last 30 Days</option>
                      <option>Last 90 Days</option>
                      <option>Last 12 Months</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 dark:text-white mb-3">Model Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confidence Threshold</label>
                    <input type="range" min="0" max="100" value="75" className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer" />
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Feature Importance</label>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-300">Recency</span>
                        <span className="text-xs font-medium text-gray-800 dark:text-white">High</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-300">Frequency</span>
                        <span className="text-xs font-medium text-gray-800 dark:text-white">Medium</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-300">Monetary Value</span>
                        <span className="text-xs font-medium text-gray-800 dark:text-white">High</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Reset to Default
                </button>
                <button className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0062cc] hover:bg-[#0052ac]">
                  Save Changes
                </button>
              </div>
            </div> */}

          </div>
        )}
      </motion.div>
    </div>
  );
};

export default UtilityPage; 